import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';

import { SbcPermissionEnum } from 'projects/workspace/src/app/administration/enums/sbc-permission.enum';
import { SubscriptionPlanEnum } from '../../../subscription-activation/subscription-activation.emum';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { ToasterService } from '../../../ui-components/toaster';
import { AdministrationsApiService } from 'projects/workspace/src/app/administration/services/administrations-api.service';
import { AuthService } from '../../../../auth/auth.service';
import { AuthUserPermissionsEnum } from '../../../../auth/auth-user-permissions.enum';
import { CommonModalsActionsEnum } from '../common-modals-actions.enum';
import { HrmService } from 'projects/workspace/src/app/hrm/hrm.service';

@Component({
  selector: 'rnpl-upgrade-plan-modal',
  templateUrl: './upgrade-plan-modal.component.html'
})
export class UpgradePlanModalComponent implements OnInit, OnDestroy {

  public userCanUpgrade: boolean = false;
  public companyProfile: CompanyProfile;
  public nextPlan: SubscriptionPlanEnum;
  public nextPlanTranslation: {[key in SubscriptionPlanEnum]?: string} = {
    [SubscriptionPlanEnum.BASIC_SUBSCRIPTION_MONTHLY]: 'SUBSCRIPTION_PLAN.PLAN_BASIC',
    [SubscriptionPlanEnum.STANDARD_SUBSCRIPTION_MONTHLY]: 'SUBSCRIPTION_PLAN.PLAN_STANDARD',
    [SubscriptionPlanEnum.PRO_SUBSCRIPTION_MONTHLY]: 'SUBSCRIPTION_PLAN.PLAN_PRO',
  };

  public freePlanFeatures: SbcPermissionEnum[] = [
    SbcPermissionEnum.INVOICES,
    SbcPermissionEnum.PAYMENTS,
    SbcPermissionEnum.SCHEDULED_PAYMENTS,
    SbcPermissionEnum.PAYMENT_CALENDAR,
    SbcPermissionEnum.CREDIT_NOTES,
    SbcPermissionEnum.OPEN_ITEMS_LISTS,
    SbcPermissionEnum.REPORTING_PERIODS,
    SbcPermissionEnum.CASHFLOW_MONITORING,
    SbcPermissionEnum.LEDGER_ACCOUNTS,
    SbcPermissionEnum.OFFERS,
    SbcPermissionEnum.SALES_PRICES,
    SbcPermissionEnum.PRODUCT_MANAGEMENT,
    SbcPermissionEnum.PARTNER_MANAGEMENT,
    SbcPermissionEnum.ACCOUNTING_DASHBOARDS,
    SbcPermissionEnum.CONTROL_CENTER,
    SbcPermissionEnum.ADMINITSTRATION,
    SbcPermissionEnum.DATA_IMPORT,
    SbcPermissionEnum.PDF_DESIGNER,
    SbcPermissionEnum.RUNPLE_AI,
    // General products
    // Categories
    // Accounting dashboards
    // Employee
  ];

  public basicPlanFeatures: SbcPermissionEnum[] = [
    ...this.freePlanFeatures,
    SbcPermissionEnum.ASSET_DIRECTORIES,
    SbcPermissionEnum.GOODS_REGISTERS,
    SbcPermissionEnum.EMAIL_IMPORT,
    SbcPermissionEnum.OPEN_BANKING,
    SbcPermissionEnum.MULTIPLE_PRICELISTS,
    SbcPermissionEnum.CUSTOM_EMAIL_SERVERS,
    SbcPermissionEnum.TEAM_USERS,
    SbcPermissionEnum.COST_CENTERS,
  ];

  public standardPlanFeatures: SbcPermissionEnum[] = [
    ...this.basicPlanFeatures,
    SbcPermissionEnum.RECURRING_INVOICES,
    SbcPermissionEnum.REPORTING_PERIODS,
    SbcPermissionEnum.VAT_REPORTING,
    SbcPermissionEnum.SALES_ORDERS,
    SbcPermissionEnum.OUTGOING_DELIVERIES,
    SbcPermissionEnum.WAREHOUSE_MANAGEMENT,
    SbcPermissionEnum.WAREHOUSE_PREFERENCES,
    SbcPermissionEnum.ECOMMERCE_INTEGRATIONS,
    SbcPermissionEnum.DOUBLE_ENTRY_ACCOUNTING,
  ];

  public proPlanFeatures: SbcPermissionEnum[] = [
    ...this.standardPlanFeatures,
    SbcPermissionEnum.VOLUME_BASED_DISCOUNTS,
    SbcPermissionEnum.PRODUCT_VARIATIONS,
    SbcPermissionEnum.RELATED_PRODUCTS,
    SbcPermissionEnum.BUNDLES,
    SbcPermissionEnum.BRANCH_OFFICES,
    SbcPermissionEnum.OPEN_API,
  ];

  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly destroy$: Subject<void> = new Subject();

  constructor(
    public router: Router,
    public authService: AuthService,
    public toasterService: ToasterService,
    public hrmService: HrmService,
    public administrationsApiService: AdministrationsApiService,
    public dialogRef: MatDialogRef<UpgradePlanModalComponent>,
    private readonly store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data: {
      validationFeature: SbcPermissionEnum,
      cbFunction: Function,
      forwardUrl: string
    }
  ) {
    this.userCanUpgrade = this.authService.getUser().permissions.administration.includes(AuthUserPermissionsEnum.ADMIN)
      || this.authService.getUser().permissions.administration.includes(AuthUserPermissionsEnum.WRITE);
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: CompanyProfile) => this.companyProfile = profile);
  }

  ngOnInit(): void {
    this.setNextPlan();
  }

  public setNextPlan(): void {
    const subscriptionPlanActive = this.companyProfile.subscriptionManagement.subscriptionPlanActive;
    if (
      subscriptionPlanActive === SubscriptionPlanEnum.FREE
      || subscriptionPlanActive === SubscriptionPlanEnum.TEAM_MONTHLY
      || subscriptionPlanActive === SubscriptionPlanEnum.ACCOUNTING_MONTHLY
    ) {
      if (this.basicPlanFeatures.includes(this.data.validationFeature)) {
        this.nextPlan = SubscriptionPlanEnum.BASIC_SUBSCRIPTION_MONTHLY;
        return;
      }

      if (this.standardPlanFeatures.includes(this.data.validationFeature)) {
        this.nextPlan = SubscriptionPlanEnum.STANDARD_SUBSCRIPTION_MONTHLY;
        return;
      }

      if (this.proPlanFeatures.includes(this.data.validationFeature)) {
        this.nextPlan = SubscriptionPlanEnum.PRO_SUBSCRIPTION_MONTHLY;
        return;
      }
    }

    if (subscriptionPlanActive === SubscriptionPlanEnum.BASIC_SUBSCRIPTION_MONTHLY) {
      if (this.standardPlanFeatures.includes(this.data.validationFeature)) {
        this.nextPlan = SubscriptionPlanEnum.STANDARD_SUBSCRIPTION_MONTHLY;
        return;
      }

      if (this.proPlanFeatures.includes(this.data.validationFeature)) {
        this.nextPlan = SubscriptionPlanEnum.PRO_SUBSCRIPTION_MONTHLY;
        return;
      }
    }

    if (subscriptionPlanActive === SubscriptionPlanEnum.STANDARD_SUBSCRIPTION_MONTHLY) {
      if (this.proPlanFeatures.includes(this.data.validationFeature)) {
        this.nextPlan = SubscriptionPlanEnum.PRO_SUBSCRIPTION_MONTHLY;
        return;
      }
    }
  }

  public submit(): void {
    if (this.submitRequest$.getValue()) { return; }
    this.submitRequest$.next(true);
    const isFreeActivePlan = this.companyProfile.subscriptionManagement.subscriptionPlanActive === SubscriptionPlanEnum.FREE;

    this.administrationsApiService.updateCompanyProfileSbcManagement({
      ...this.companyProfile,
      subscriptionManagement: {
        ...this.companyProfile.subscriptionManagement,
        subscriptionPlan: this.nextPlan
      }
    })
      .pipe(
        switchMap(() => this.hrmService.getUserProfileAndBlueprintsV2(this.authService.getUser().id, false, true)),
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.toasterService.notify({message: 'COMMON.CHANGES_SAVED', type: 'success'});
        if (!isFreeActivePlan) {
          if (this.data.forwardUrl) {
            this.router.navigate([this.data.forwardUrl]);
          }
          if (this.data.cbFunction) {
            this.data.cbFunction();
          }
        }
        this.close();
      });
  }

  public close(): void {
    this.dialogRef.close(CommonModalsActionsEnum.CLOSE);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}

